import { useCallback, useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { useClient } from '@/services/client/useClient';
import {
  GocardlessVerifyBankRequest,
  GocardlessVerifyBankResponse,
  useGocardlessVerifyBankMutation,
} from '@/api/payments/gocardlessVerifyBank';

import { GocardlessState, setBankName } from './gocardlessSlice';

const buildVerifyBankPayload = (
  gocardlessState: GocardlessState,
  client: string,
): GocardlessVerifyBankRequest => ({
  client,
  accountNumber: gocardlessState.accountNumber,
  sortCode: gocardlessState.sortCode,
});

export function useGocardlessVerifyBank() {
  const onSuccess = useRef<((data: GocardlessVerifyBankResponse) => void) | undefined>(undefined);
  const onError = useRef<((error?: unknown) => void) | undefined>(undefined);

  const dispatch = useAppDispatch();
  const gocardlessState = useAppSelector(state => state.gocardless);

  const [verifyBankTrigger, {
    isLoading,
    data,
    error,
  }] = useGocardlessVerifyBankMutation();

  const { getClientId } = useClient();

  // Trigger payment-service verify bank
  const triggerVerifyBankApi = useCallback(async (
    callbacks?: {
      onSuccess?: (data: GocardlessVerifyBankResponse) => void,
      onError?: (error?: unknown) => void,
    }
  ) => {
    onSuccess.current = callbacks?.onSuccess;
    onError.current = callbacks?.onError;

    try {
      const payload = buildVerifyBankPayload(
        gocardlessState,
        getClientId(),
      );

      await verifyBankTrigger(payload);
    } catch (err) {
      logger.error('Error verifying your bank account details');
      logger.error(err);
      if (onError.current) {
        onError.current(err);
      }
    }
  }, [gocardlessState, getClientId, verifyBankTrigger]);

  useEffect(() => {
    // Wait until payment-service is complete
    if (isLoading || (!data && !error)) {
      return;
    }

    // Handle payment-service errors
    if (!!error || data?.message !== 'success' || !data?.data?.bank) {
      logger.error('Error verifying your bank account details');
      logger.error(error);

      if (onError.current) {
        onError.current(error);
      }
      return;
    }

    if (onSuccess.current) {
      onSuccess.current(data);
      dispatch(setBankName(data.data.bank));
    }
  }, [isLoading, data, error, onSuccess, onError, dispatch]);

  return {
    triggerVerifyBankApi,
    bankVerifyIsLoading: isLoading,
    bankVerifyData: data,
    bankVerifyError: error,
  };
}
